import { logMetric, MetricType } from '@cbhq/client-analytics';
import { doOncePerSession } from '@cbhq/commerce-shared/utils/session/doOncePerSession';

import {
  CriticalStepName,
  ErrorHandler,
  FundsAccessMetricName,
  FundsAccessTags,
} from ':fat/analytics/metricDefinitions';
import { getPageKeyFromPath } from ':fat/analytics/pageKey';

const merchantMetricPrefix = 'funds_access_tool';

export type LogFundsAccessCountMetricParams = {
  metricName: FundsAccessMetricName;
  tags?: FundsAccessTags;
};

// Private method that just adds the commerce_merchant prefix to all logMetric calls and enforces defined metric names
// and tags from metricDefinitions.ts
function logFundsAccessMetric({
  metricName,
  metricType,
  value,
  tags,
}: {
  metricName: string;
  metricType: MetricType;
  value: number;
  tags?: FundsAccessTags;
}) {
  logMetric({
    metricName: `${merchantMetricPrefix}_${metricName}`,
    metricType,
    value,
    tags: {
      page_key: getPageKeyFromPath(window.location.href) ?? 'unknown',
      ...tags,
    },
  });
}

/**
 * Increments a count metric with the given name.
 */
function logFundsAccessCountMetric({ metricName, tags }: LogFundsAccessCountMetricParams) {
  logFundsAccessMetric({
    metricName,
    metricType: MetricType.count,
    value: 1,
    tags,
  });
}

export type LogCriticalStepProps = { step: CriticalStepName; tags?: FundsAccessTags };

/**
 * This helper publishes a raw error metric to count the number of errors, as well as a session_error metric to only
 * count the first error per session.
 */
export function logFundsAccessError(errorHandler: ErrorHandler) {
  logFundsAccessCriticalStep({
    step: 'session_error',
    tags: { error_handler: errorHandler },
  });
  logFundsAccessCountMetric({
    metricName: 'unexpected_error',
    tags: { error_handler: errorHandler },
  });
}

/**
 * Logs a critical_step metric with the given step tag, critical steps can only occur once per page session
 */
export function logFundsAccessCriticalStep({ step, tags }: LogCriticalStepProps) {
  const idemKey = `critical_step.${step}`;

  doOncePerSession(
    logFundsAccessCountMetric,
    idemKey,
  )({
    metricName: 'critical_step',
    tags: { step, ...tags },
  });
}
